import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { isTeacher } from '../utils/roleHelpers';
import api from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import GroupForm from '../components/GroupForm';
import EmptyState from '../components/EmptyState';
import { checkGroupRequirements } from '../utils/diagnosticHelper';
import './Groups.css';

const Groups = () => {
  const { user } = useAuth();
  const isUserTeacher = isTeacher(user);
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Estado local para el input
  const searchInputRef = useRef(null); // Ref para mantener el foco
  const [filterDiscipline, setFilterDiscipline] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  // Debounce para la búsqueda
  useEffect(() => {
    // Si el input está vacío, actualizar inmediatamente
    if (searchInput === '') {
      setSearch('');
      return;
    }
    
    const timer = setTimeout(() => {
      setSearch(searchInput);
    }, 200); // Esperar 200ms después de que el usuario deje de escribir (reducido para mejor UX)

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Manejar el cambio de búsqueda sin perder el foco
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);
  }, []);

  const { data: groups, isLoading, error } = useQuery(
    ['groups', search, filterDiscipline],
    async () => {
      const params = {};
      if (search) params.search = search;
      if (filterDiscipline) params.discipline_id = filterDiscipline;
      const response = await api.get('/groups', { params });
      return response.data;
    },
    {
      keepPreviousData: true, // Mantener datos anteriores mientras carga nuevos
      staleTime: 5000, // Considerar datos frescos por 5 segundos
      onError: (err) => {
        console.error('Error al cargar grupos:', err);
      }
    }
  );

  const { data: disciplines } = useQuery(
    'disciplines',
    async () => {
      const response = await api.get('/disciplines');
      return response.data;
    }
  );

  const { data: levels } = useQuery('levels', async () => {
    const response = await api.get('/levels');
    return response.data;
  });

  const groupDiagnostic = React.useMemo(() => {
    return checkGroupRequirements({
      disciplines: disciplines || [],
      levels: levels || [],
    });
  }, [disciplines, levels]);

  const selectedGroups = (groups || []).filter((group) => selectedIds.includes(group.id));

  const toggleGroupSelection = (groupId) => {
    setSelectedIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  };

  const toggleAllVisible = () => {
    const visibleIds = (groups || []).map((group) => group.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : visibleIds);
  };

  const createGroupMutation = useMutation(
    (data) => api.post('/groups', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('groups');
        toast.success('Grupo creado exitosamente!');
        setIsModalOpen(false);
        setEditingGroup(null);
      },
      onError: (err) => {
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail)) {
          errorDetail.forEach(e => toast.error(`${e.loc[1]}: ${e.msg}`));
        } else {
          toast.error(errorDetail || 'Error al crear grupo');
        }
      },
    }
  );

  const updateGroupMutation = useMutation(
    ({ id, data }) => api.put(`/groups/${id}`, data),
    {
      onSuccess: () => {
        // Invalidar y refetch para asegurar que los datos se actualicen
        queryClient.invalidateQueries(['groups', search, filterDiscipline]);
        queryClient.invalidateQueries('groups');
        toast.success('Grupo actualizado exitosamente!');
        setIsModalOpen(false);
        setEditingGroup(null);
      },
      onError: (err) => {
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail)) {
          errorDetail.forEach(e => toast.error(`${e.loc[1]}: ${e.msg}`));
        } else {
          toast.error(errorDetail || 'Error al actualizar grupo');
        }
      },
    }
  );

  const toggleGroupStatusMutation = useMutation(
    ({ id, is_active }) => api.put(`/groups/${id}`, { is_active }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('groups');
        toast.success('Estado del grupo actualizado!');
      },
      onError: (err) => {
        toast.error(err.response?.data?.detail || 'Error al cambiar estado');
      },
    }
  );

  const handleCreateClick = () => {
    setEditingGroup(null);
    setIsModalOpen(true);
  };

  // Abrir modal automáticamente si viene con action=create
  useEffect(() => {
    if (searchParams.get('action') === 'create') {
      handleCreateClick();
      // Limpiar el parámetro de la URL
      navigate('.', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleEditClick = (group) => {
    setEditingGroup(group);
    setIsModalOpen(true);
  };

  const handleSubmit = (data) => {
    if (editingGroup) {
      updateGroupMutation.mutate({ id: editingGroup.id, data });
    } else {
      createGroupMutation.mutate(data);
    }
  };

  const handleToggleStatus = (group) => {
    const action = group.is_active ? 'desactivar' : 'activar';
    setConfirmDialog({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Grupo`,
      message: `¿Estás seguro de ${action} el grupo "${group.name}"?`,
      onConfirm: () => {
        toggleGroupStatusMutation.mutate({
          id: group.id,
          is_active: !group.is_active,
        });
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null),
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      cancelText: 'Cancelar',
    });
  };

  const handleBulkToggle = () => {
    if (selectedGroups.length === 1) {
      handleToggleStatus(selectedGroups[0]);
      return;
    }

    const allActive = selectedGroups.every((group) => group.is_active);
    const count = selectedGroups.length;
    const action = allActive ? 'desactivar' : 'activar';
    setConfirmDialog({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} ${count} grupos`,
      message: allActive
        ? `¿Desactivar ${count} grupos? Dejarán de estar disponibles para nuevas matrículas.`
        : `¿Activar ${count} grupos?`,
      onConfirm: () => {
        selectedGroups.forEach((group) => {
          toggleGroupStatusMutation.mutate({
            id: group.id,
            is_active: !allActive,
          });
        });
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null),
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      cancelText: 'Cancelar',
    });
  };

  const getDayName = (dayOfWeek) => {
    const days = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    return days[dayOfWeek] || 'N/A';
  };

  if (isLoading) {
    return <div className="loading">Cargando grupos...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error">
          <h3>❌ Error al cargar grupos</h3>
          <p>{error.response?.data?.detail || error.message || 'Error desconocido'}</p>
          <button 
            className="btn btn-primary" 
            onClick={() => queryClient.invalidateQueries('groups')}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>Gestión de Grupos</h2>
        {!isUserTeacher && (
          <button className="btn btn-primary" onClick={handleCreateClick}>
            + Nuevo Grupo
          </button>
        )}
      </div>
      
      {isUserTeacher && (
        <div className="info-message" style={{ 
          marginBottom: '20px', 
          padding: '12px 16px', 
          backgroundColor: '#e3f2fd', 
          borderLeft: '4px solid #2196f3',
          borderRadius: '4px'
        }}>
          ℹ️ Como profesor, solo puedes ver los grupos asignados a ti. No puedes crear, editar ni desactivar grupos.
        </div>
      )}

      <div className="card">
        <div className="filters-bar">
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Buscar por nombre o código..."
            value={searchInput}
            onChange={handleSearchChange}
            onKeyDown={(e) => {
              // Prevenir que Enter recargue la página o cause submit
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                setSearch(searchInput);
                // Mantener el foco en el input
                setTimeout(() => {
                  if (searchInputRef.current) {
                    searchInputRef.current.focus();
                  }
                }, 0);
                return false;
              }
            }}
            className="search-input"
            autoComplete="off"
          />
          <select
            value={filterDiscipline}
            onChange={(e) => setFilterDiscipline(e.target.value)}
            className="filter-select"
          >
            <option value="">Todas las disciplinas</option>
            {disciplines?.map((discipline) => (
              <option key={discipline.id} value={discipline.id}>
                {discipline.name}
              </option>
            ))}
          </select>
        </div>

        {!isUserTeacher && (
          <div className="groups-bulk-bar">
            <button
              type="button"
              className="groups-bulk-btn"
              onClick={() => handleEditClick(selectedGroups[0])}
              disabled={selectedGroups.length !== 1}
            >
              <span className="groups-bulk-icon" aria-hidden="true">✏️</span>
              Editar
            </button>
            <button
              type="button"
              className="groups-bulk-btn is-warning"
              onClick={handleBulkToggle}
              disabled={!selectedGroups.length || toggleGroupStatusMutation.isLoading}
            >
              <span className="groups-bulk-icon" aria-hidden="true">⏸️</span>
              {selectedGroups.length === 1 && !selectedGroups[0].is_active ? 'Activar' : 'Desactivar'}
            </button>
          </div>
        )}

        <div className="table-container groups-table-container">
          <table className="table groups-table">
            <thead>
              <tr>
                {!isUserTeacher && (
                  <th className="groups-col-check">
                    <input
                      type="checkbox"
                      aria-label="Seleccionar grupos visibles"
                      checked={groups?.length > 0 && groups.every((group) => selectedIds.includes(group.id))}
                      onChange={toggleAllVisible}
                    />
                  </th>
                )}
                <th>Nombre</th>
                <th>Código</th>
                <th>Disciplina</th>
                <th>Nivel</th>
                <th>Horario</th>
                <th>Profesor</th>
                <th>Sede</th>
                <th>Capacidad</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {groups && groups.length > 0 ? (
                groups.map((group) => (
                  <tr key={group.id} className={selectedIds.includes(group.id) ? 'is-selected' : ''}>
                    {!isUserTeacher && (
                      <td className="groups-col-check">
                        <input
                          type="checkbox"
                          aria-label={`Seleccionar ${group.name}`}
                          checked={selectedIds.includes(group.id)}
                          onChange={() => toggleGroupSelection(group.id)}
                        />
                      </td>
                    )}
                    <td>{group.name}</td>
                    <td><code className="group-code">{group.code}</code></td>
                    <td>
                      {group.discipline ? (
                        <span className="discipline-badge" style={{ borderLeftColor: group.discipline.color || '#3498db' }}>
                          {group.discipline.icon || '⚽'} {group.discipline.name}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>{group.level?.name || '-'}</td>
                    <td>
                      {group.schedules && group.schedules.length > 0 ? (
                        <div className="schedule-info">
                          {group.schedules.map(s => (
                            <div key={s.id} className="schedule-info-row">
                              <span className="schedule-day-pill">{getDayName(s.day_of_week).slice(0, 3)}</span>
                              <span className="schedule-time">{s.start_time} - {s.end_time}</span>
                            </div>
                          ))}
                        </div>
                      ) : group.schedule ? (
                        <div className="schedule-info">
                          <div>{getDayName(group.schedule.day_of_week)}</div>
                          <div className="schedule-time">
                            {group.schedule.start_time} - {group.schedule.end_time}
                          </div>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      {group.teacher ? (
                        <span className="teacher-name">{group.teacher.full_name}</span>
                      ) : group.teacher_id ? (
                        `ID: ${group.teacher_id}`
                      ) : (
                        'Sin asignar'
                      )}
                    </td>
                    <td>
                      {group.location ? (
                        <span className="location-badge">
                          📍 {group.location.name} {group.location.code ? `(${group.location.code})` : ''}
                        </span>
                      ) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>Sin sede</span>
                      )}
                    </td>
                    <td>
                      {`${group.active_enrollments ?? 0}/${group.capacity ?? 20}`}
                    </td>
                    <td>
                      <span className={`badge ${group.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {group.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isUserTeacher ? 9 : 10} style={{ padding: 0, border: 'none' }}>
                    <EmptyState
                      icon="👨‍🏫"
                      title="Aún no hay grupos registrados"
                      message="Los grupos organizan a los estudiantes por disciplina, nivel y horario. El horario se define al crear el grupo. Para empezar, necesitas:"
                      requirements={groupDiagnostic.requirements}
                      primaryAction={groupDiagnostic.allMet ? handleCreateClick : null}
                      primaryActionLabel="+ Crear Primer Grupo"
                      hint={groupDiagnostic.allMet 
                        ? "¡Todo listo! Puedes crear tu primer grupo ahora."
                        : `Completa ${groupDiagnostic.count.pending} requisito(s) más para poder crear grupos.`}
                      className="compact"
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        title={editingGroup ? 'Editar Grupo' : 'Nuevo Grupo'}
        closeOnOverlayClick={false}
        size="lg"
      >
        <GroupForm
          initialData={editingGroup}
          onSubmit={handleSubmit}
          onCancel={() => setIsModalOpen(false)}
          isLoading={createGroupMutation.isLoading || updateGroupMutation.isLoading}
        />
      </Modal>

      {confirmDialog && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onClose={confirmDialog.onCancel}
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
        />
      )}
    </div>
  );
};

export default Groups;
